import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FileText, Clock, ClipboardCheck, Truck, Activity, Plus, FileSignature, Store, Receipt, Sun, ReceiptText, Calculator, ShoppingBag } from "lucide-react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { useAuth } from "@/contexts/AuthContext";
import { CARD_IMPORT_ROLES } from "@/constants/roles";
import { fmtDate } from "@/lib/utils";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const KPI = ({ icon: Icon, label, value, sub, subClass }) => (
  <div className="px-5 py-4 border-l border-slate-100 first:border-l-0" data-testid={`kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}>
    <div className="flex items-center gap-2 text-slate-500 text-[13px] font-semibold">
      <Icon size={16} className="text-sky-600" /> {label}
    </div>
    <div className="text-[30px] font-extrabold tracking-tight mt-2">{value}</div>
    <div className={`text-[12px] mt-0.5 ${subClass || "text-slate-400"}`}>{sub}</div>
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAccountingRole = CARD_IMPORT_ROLES.includes(user?.role);

  const load = () => api.get("/contractor/dashboard").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  if (!data) return <div className="p-8 text-slate-400 text-sm">Loading dashboard…</div>;

  const actions = [
    { label: "New Purchase Order", icon: FileText, action: () => navigate("/contractor/purchase-requests/new") },
    { label: "Log an Expense", icon: ReceiptText, action: () => navigate("/contractor/expense-log/new") },
    { label: "Create RFQ", icon: FileSignature, action: () => navigate("/contractor/rfqs/new") },
    { label: "Browse Catalog", icon: Store, action: () => navigate("/contractor/catalog") },
    { label: "View Purchase Orders", icon: Receipt, action: () => navigate("/contractor/purchase-orders") },
  ];

  return (
    <div className="p-6 md:p-8" data-testid="contractor-dashboard">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight flex items-center gap-2.5">
            <Sun size={22} className="text-sky-500" /> Good morning
          </h1>
          <p className="text-slate-500 text-sm mt-1">Here's what's happening in your company.</p>
        </div>
        <button
          onClick={() => navigate("/contractor/purchase-requests/new")}
          data-testid="new-request-button"
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold transition-colors"
        >
          <Plus size={14} /> New request
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 mt-6 border-t border-b border-slate-100">
        <KPI icon={FileText} label="Purchase Requests" value={data.kpis.open_prs} sub="Open" />
        <KPI icon={Clock} label="Awaiting Approval" value={data.kpis.awaiting_approval} sub="Requests" />
        <KPI icon={ClipboardCheck} label="Purchase Orders" value={data.kpis.open_pos} sub="Open" />
        <KPI icon={Truck} label="Deliveries" value={data.kpis.deliveries} sub="In progress" />
      </div>

      {user?.purchase_policy && (
        <div className="mt-4 text-[12.5px] text-slate-500" data-testid="my-purchase-policy">
          Your purchase policy: <span className="font-semibold text-slate-700">
            {user.purchase_policy.restriction === "threshold"
              ? `Purchases over $${Number(user.purchase_policy.threshold_amount || 0).toLocaleString("en-US")} require approval`
              : user.purchase_policy.restriction === "all_purchases"
              ? "All purchases require approval"
              : "Purchases never require approval"}
          </span>
        </div>
      )}

      {isAccountingRole && data.kpis.accounting_pending_review > 0 && (
        <div className="mt-6 border border-sky-200 bg-sky-50 rounded-xl p-4" data-testid="accounting-pending-review-tile">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 text-sky-800 font-bold text-[13.5px]">
              <Calculator size={16} /> Waiting for Accounting Approval ({data.kpis.accounting_pending_review})
            </div>
            <Link to="/contractor/accounting" className="text-[12.5px] font-semibold text-sky-700">Open Accounting</Link>
          </div>
        </div>
      )}

      {data.approved_ready_to_checkout && data.approved_ready_to_checkout.length > 0 && (
        <div className="mt-6 border border-emerald-200 bg-emerald-50 rounded-xl p-4" data-testid="approved-ready-to-checkout-tile">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 text-emerald-800 font-bold text-[13.5px]">
              <ShoppingBag size={16} /> Approved — Ready to Check Out ({data.kpis.approved_ready_to_checkout})
            </div>
          </div>
          <div className="mt-3 space-y-1.5">
            {data.approved_ready_to_checkout.map((pr) => (
              <div key={pr.id} onClick={() => navigate(`/contractor/purchase-requests/${pr.id}`)} className="text-[12.5px] flex justify-between cursor-pointer text-emerald-900" data-testid={`approved-checkout-row-${pr.pr_number}`}>
                <div><span className="mono font-semibold">{pr.pr_number}</span> · {pr.context_label || pr.vendor_display_name || "—"}</div>
                <div className="mono font-medium">{fmt(pr.amount)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.outside_purchases_pending && data.outside_purchases_pending.length > 0 && (
        <div className="mt-6 border border-amber-200 bg-amber-50 rounded-xl p-4" data-testid="outside-purchases-pending-tile">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 text-amber-800 font-bold text-[13.5px]">
              <ReceiptText size={16} /> Expense Drafts Not Yet Submitted ({data.kpis.outside_purchases_pending})
            </div>
            <Link to="/contractor/expense-log" className="text-[12.5px] font-semibold text-amber-700">View All</Link>
          </div>
          <div className="mt-3 space-y-1.5">
            {data.outside_purchases_pending.map((op) => (
              <div key={op.id} onClick={() => navigate(`/contractor/expense-log/${op.id}`)} className="text-[12.5px] flex justify-between cursor-pointer text-amber-900" data-testid={`outside-purchase-pending-row-${op.op_number}`}>
                <div><span className="mono font-semibold">{op.po_number || op.op_number}</span> · {op.vendor_name_freetext || "Vendor on file"}</div>
                <div className="mono font-medium">{fmt(op.total)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-9 mt-8">
        <div>
          <div className="flex items-center justify-between">
            <div className="text-[15px] font-bold">Recent Purchase Requests</div>
            <Link to="/contractor/purchase-orders" className="text-[12.5px] font-semibold text-sky-600">View All</Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full mt-3 text-[12.5px]" data-testid="recent-prs-table">
              <thead>
                <tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
                  <th className="text-left pb-2">PR #</th>
                  <th className="text-left pb-2">Job</th>
                  <th className="text-right pb-2">Amount</th>
                  <th className="text-left pb-2 pl-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_prs.map((pr) => (
                  <tr key={pr.id} onClick={() => navigate(`/contractor/purchase-requests/${pr.id}`)} className="cursor-pointer hover:bg-slate-50" data-testid={`pr-row-${pr.pr_number}`}>
                    <td className="py-2.5 border-t border-slate-100 mono text-sky-600 font-medium">{pr.pr_number}</td>
                    <td className="py-2.5 border-t border-slate-100">{pr.context_label || "—"}</td>
                    <td className="py-2.5 border-t border-slate-100 text-right mono font-medium">{fmt(pr.amount)}</td>
                    <td className="py-2.5 border-t border-slate-100 pl-3"><StatusBadge status={pr.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="md:pl-9 md:border-l border-slate-100">
          <div className="flex items-center justify-between">
            <div className="text-[15px] font-bold">Recent Purchase Orders</div>
            <Link to="/contractor/purchase-orders" className="text-[12.5px] font-semibold text-sky-600">View All</Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full mt-3 text-[12.5px]" data-testid="recent-pos-table">
              <thead>
                <tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
                  <th className="text-left pb-2">PO #</th>
                  <th className="text-left pb-2">Vendor</th>
                  <th className="text-right pb-2">Amount</th>
                  <th className="text-left pb-2 pl-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_pos.map((po) => (
                  <tr key={po.id} onClick={() => navigate(`/contractor/purchase-orders/${po.id}`)} className="cursor-pointer hover:bg-slate-50" data-testid={`po-row-${po.po_number}`}>
                    <td className="py-2.5 border-t border-slate-100 mono text-sky-600 font-medium">{po.po_number}</td>
                    <td className="py-2.5 border-t border-slate-100">{po.vendor_name}</td>
                    <td className="py-2.5 border-t border-slate-100 text-right mono font-medium">{fmt(po.amount)}</td>
                    <td className="py-2.5 border-t border-slate-100 pl-3"><StatusBadge status={po.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-8 mt-8 pt-7 border-t border-slate-100">
        <div>
          <div className="text-[14.5px] font-bold">Deliveries Awaiting Confirmation</div>
          <div className="mt-3 space-y-2.5">
            {data.deliveries.length === 0 && <div className="text-[12px] text-slate-400">No pending deliveries.</div>}
            {data.deliveries.map((d) => (
              <div key={d.id} className="text-[12px] border-t border-slate-100 pt-2.5 flex justify-between">
                <div><span className="mono text-sky-600 font-medium">{d.po_number}</span> · {d.vendor_name}</div>
                <div className="text-slate-500">{fmtDate(d.expected_delivery)}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:pl-8 md:border-l border-slate-100">
          <div className="text-[14.5px] font-bold">Awaiting Vendor Response</div>
          <div className="mt-3 space-y-2.5" data-testid="dashboard-awaiting-vendor-response">
            {(data.awaiting_vendor_response || []).length === 0 && <div className="text-[12px] text-slate-400">Nothing waiting on a vendor.</div>}
            {(data.awaiting_vendor_response || []).map((po) => (
              <div key={po.id} onClick={() => navigate(`/contractor/purchase-orders/${po.id}`)} className="text-[12px] border-t border-slate-100 pt-2.5 flex justify-between cursor-pointer" data-testid={`awaiting-vendor-response-row-${po.po_number}`}>
                <div><span className="mono text-sky-600 font-medium">{po.po_number}</span> · {po.vendor_name}</div>
                <div className="text-slate-500">{fmtDate(po.sent_at)}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:pl-8 md:border-l border-slate-100">
          <div className="text-[14.5px] font-bold">My Pending Approvals</div>
          <div className="mt-3 space-y-2.5">
            {data.pending_approvals.length === 0 && <div className="text-[12px] text-slate-400">Nothing pending your approval.</div>}
            {data.pending_approvals.map((pr) => (
              <div key={pr.id} onClick={() => navigate(`/contractor/purchase-requests/${pr.id}`)} className="text-[12px] border-t border-slate-100 pt-2.5 flex justify-between cursor-pointer">
                <div><span className="mono text-sky-600 font-medium">{pr.pr_number}</span></div>
                <div className="mono font-medium">{fmt(pr.amount)}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:pl-8 md:border-l border-slate-100">
          <div className="text-[14.5px] font-bold">Recent Activity</div>
          <div className="mt-3 space-y-2.5">
            {data.activity.map((a) => (
              <div key={a.id} className="text-[12px] border-t border-slate-100 pt-2.5 flex gap-2">
                <Activity size={12} className="text-sky-600 mt-0.5 flex-shrink-0" />
                <div>{a.action}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-9 pt-7 border-t border-slate-100">
        <div className="text-[15px] font-bold">Quick Actions</div>
        <div className="grid grid-cols-2 md:grid-cols-4 mt-4 border border-slate-100 rounded-xl overflow-hidden">
          {actions.map((a) => (
            <button key={a.label} onClick={a.action} data-testid={`quick-action-${a.label.replace(/\s+/g, "-").toLowerCase()}`} className="p-5 flex flex-col items-center gap-2.5 border-l border-slate-100 first:border-l-0 hover:bg-slate-50 transition-colors">
              <a.icon size={20} className="text-sky-600" />
              <div className="text-[13px] font-semibold text-center">{a.label}</div>
            </button>
          ))}
        </div>
      </div>

    </div>
  );
}
